/**
 * MSW Configuration Types
 */

import { ENDPOINTS_KEYS as BOOKING_ENDPOINTS_KEYS } from '../handlers/bookings';
import { ENDPOINTS_KEYS as SUBSCRIPTION_ENDPOINTS_KEYS } from '../handlers/subscriptions';
import { ENDPOINTS_KEYS as BENEFICIARY_ENDPOINTS_KEYS } from '../handlers/beneficiaries';
import { ENDPOINTS_KEYS as BILLING_ENDPOINTS_KEYS } from '../handlers/billing';
import { ENDPOINTS_KEYS as GEOGRAPHY_ENDPOINTS_KEYS } from '../handlers/geography';
import { ENDPOINTS_KEYS as CAMPAIGN_ENDPOINTS_KEYS } from '../handlers/campaigns';

/**
 * Configuration for MSW mocking
 */
export interface MSWConfig {
  /** Endpoint-specific configuration */
  endpoints?: EndpointConfig;
  /** Preset configuration (e.g., "all" to enable all endpoints) */
  preset?: 'all' | 'none';
}

/**
 * Configuration for individual endpoints
 */
export interface EndpointConfig {
  // Booking endpoints
  TRIP_SEARCH?: boolean;
  TRIP_SEARCH_PARAMS?: boolean;
  QUOTATION?: boolean;
  BOOKING?: boolean;
  CREATE_BOOKING?: boolean;
  BOOKING_PAYMENTS?: boolean;
  BOOKING_PAYMENT_AUTHENTICATION_NOTIFICATIONS?: boolean;
  SUBSCRIPTION_PASSENGERS?: boolean;
  SUBSCRIPTION_PASSENGERS_EVALUATION?: boolean;

  // Subscription endpoints
  SUBSCRIPTION?: boolean;
  CANCEL_SUBSCRIPTION?: boolean;
  SUBSCRIPTION_BENEFICIARIES?: boolean;

  // Payment endpoints
  BILLING_INFO?: boolean;
  RECEIPT?: boolean;
  PAYMENT_BY_PROVIDER?: boolean;
  SUBSCRIPTIONS_PAYMENT_INTENT?: boolean;
  PAYERS?: boolean;
  INVOICES?: boolean;

  // Campaign endpoints
  CAMPAIGN_LIST?: boolean;

  // Extensible for future endpoints
  [key: string]: boolean | undefined;
}

/**
 * Check if any endpoint is enabled in the configuration
 * @param endpoints - Endpoint configuration object
 * @returns True if any endpoint is explicitly enabled
 */
function hasEnabledEndpoints(endpoints?: EndpointConfig): boolean {
  if (!endpoints) {
    return false;
  }
  return Object.values(endpoints).some(value => value === true);
}

/**
 * Check if MSW should be enabled based on configuration
 * @param config - MSW configuration
 * @returns True if MSW should be enabled
 */
function isMSWEnabled(config: MSWConfig): boolean {
  return config.preset === 'all' || hasEnabledEndpoints(config.endpoints);
}

/**
 * Validate and normalize MSW configuration
 * @param config - MSW configuration to validate
 * @returns Validated MSW configuration
 */
export function validateConfig(config: MSWConfig): MSWConfig {
  const validated: MSWConfig = {
    preset: config.preset,
    endpoints: config.endpoints,
  };

  // If preset is "all", enable all endpoints
  if (validated.preset === 'all') {
    // Merge all endpoint keys from handlers
    const allEndpointKeys = [
      ...Object.values(BOOKING_ENDPOINTS_KEYS),
      ...Object.values(SUBSCRIPTION_ENDPOINTS_KEYS),
      ...Object.values(BENEFICIARY_ENDPOINTS_KEYS),
      ...Object.values(BILLING_ENDPOINTS_KEYS),
      ...Object.values(GEOGRAPHY_ENDPOINTS_KEYS),
      ...Object.values(CAMPAIGN_ENDPOINTS_KEYS),
    ];

    // Enable all known endpoints
    const allEndpoints = allEndpointKeys.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as EndpointConfig);

    validated.endpoints = {
      ...allEndpoints,
      ...validated.endpoints, // Allow explicit overrides
    };
  }

  return validated;
}

/**
 * Check if a specific endpoint should be mocked
 * @param config - MSW configuration
 * @param endpointName - Name of the endpoint to check
 * @returns True if the endpoint should be mocked, false otherwise
 */
export function isEndpointEnabled(config: MSWConfig, endpointName: string): boolean {
  // MSW must be enabled (auto-determined by preset or endpoints)
  if (!isMSWEnabled(config)) {
    return false;
  }

  // If preset is "all" and endpoint not explicitly disabled
  if (config.preset === 'all') {
    return true;
  }

  // Check explicit endpoint configuration
  if (config.endpoints && endpointName in config.endpoints) {
    return config.endpoints[endpointName] === true;
  }

  return false;
}
